<template>
  <!-- Отметки событий внутри .ly-cam: координаты — те же px .ly-stage, что у карточек (К6),
       глубина — translateZ(-z) из layoutUnits. data-sz/data-kind повторяют разметку прототипа,
       чтобы сцена могла включить отметки в своё угасание по глубине. -->
  <div class="ly-evm" v-if="on">
    <!-- заливку «заливки» и «картотеки» и выписку выдвинутой карточки рисует сама карточка
         в сцене: так цвет гаснет и выезжает вместе с ней -->
    <!-- стыки: скобка на полу между соседями и подпись со сдвигом блока оттока -->
    <template v-for="j in jointMarks" :key="'j' + j.i">
      <div class="ly-evm-jnt" :style="j.bar" :data-sz="-j.a" data-kind="isl"></div>
      <div class="ly-ev3 is-joint is-lab" :style="j.lab" :data-sz="-j.mid" data-kind="evt">
        <i class="ly-ev3-stem"></i>
        <span class="ly-ev3-cap" data-lab :title="j.title">
          <b>{{ j.icon }}</b><span class="ly-ev3-txt">{{ j.text }}</span><s>{{ j.pause }}</s>
        </span>
      </div>
    </template>

    <!-- вехи / столбы -->
    <div v-for="m in marks" :key="'e' + m.e.id" class="ly-ev3" :class="m.cls" :style="m.style"
         :data-sz="-m.z" data-kind="evt" :data-ev="m.e.id">
      <i class="ly-ev3-stem"></i>
      <span class="ly-ev3-cap" data-lab :title="m.e.note" @click.stop="pick(m.e.id)">
        <u>{{ m.e.id + 1 }}</u><b>{{ EV[m.e.kind].i }}</b><span class="ly-ev3-txt">{{ m.e.title }}</span><s>{{ m.date }}</s>
      </span>
    </div>
  </div>
</template>

<script>
import { joints, dayLabel } from '@/utils/layers';

// Типы событий deriveEvents: цвет, значок, имя. Внешних («письмо», «звонок») нет — они
// в прототипе были случайными, в данных их нет.
const EV = {
  conv: { c: 'var(--ev-goal)', i: '✓', n: 'конверсия' },
  chg: { c: 'var(--ev-drop)', i: '⇄', n: 'изменение' },
  mile: { c: 'var(--ev-peak)', i: '▲', n: 'веха' },
};
function plural(n, f) {
  const a = Math.abs(n) % 100, b = a % 10;
  return a > 10 && a < 20 ? f[2] : b > 1 && b < 5 ? f[1] : b === 1 ? f[0] : f[2];
}
const BILL = z => `translateZ(${-z}px) rotateY(var(--by,0deg)) rotateX(var(--bx,0deg))`;
// Смесь цвета события с прозрачным: color-mix вместо чтения rgb из вычисленных стилей —
// так столб сам перекрашивается при смене темы.
const mix = (c, a) => `color-mix(in srgb, ${c} ${Math.round(a * 100)}%, transparent)`;
const LANE_FAR = 230;          // дальше этого по глубине подписи уже не наезжают

// Первая дорожка, где рядом по глубине никого, иначе самая свободная (прототип 5006–5013).
function laneOf(lanes, z) {
  let lane = 0, far = -1;
  for (let L = 0; L < lanes.length; L++) {
    if (lanes[L] === null) { lane = L; break; }
    const d = Math.abs(z - lanes[L]);
    if (d >= LANE_FAR) { lane = L; break; }
    if (d > far) { far = d; lane = L; }
  }
  lanes[lane] = z;
  return lane;
}

export default {
  name: 'LayerEventMarks',
  props: {
    geom: { type: Object, default: null },
    layout: { type: Object, default: null },
    events: { type: Array, default: () => [] },
    mode: { default: null },
    selected: { default: null },                      // id события или null
    rangeShift: { type: Number, default: 0 },         // сдвиг ряда вправо при открытом окне ленты, px
    labelOffset: { type: Number, default: 84 },       // от нижней кромки столбца до ярлыков событий, px
    jointsOn: { type: Boolean, default: true },       // «стыки»: по времени и без сортировки по блоку (П 4895)
    numOf: { type: Function, default: null },         // хронологический номер сессии по всему столбцу
    tileW: { type: Number, default: null },           // ширина плитки (TW: в корешках узкая)
    selectedIndex: { type: Number, default: null },   // выбранная карточка: она всегда во всю ширину
  },
  emits: ['select-event'],
  data() { return { EV }; },
  computed: {
    items() { return (this.layout && this.layout.items) || []; },
    on() { return !!this.geom && this.items.length > 0 && ['pins', 'tint', 'card', 'joints'].includes(this.mode); },
    gapMode() { return this.mode === 'tint' || this.mode === 'card'; },
    colBottom() { return this.geom.colTop + this.geom.colH; },
    floorTop() { return this.colBottom + this.labelOffset; },
    colLeft() { return this.geom.colLeft + this.rangeShift; },
    posOf() {
      const m = new Map();
      this.items.forEach((it, k) => m.set(it.unit.id, k));
      return m;
    },
    // Событие → карточки ряда: внутри сессии — её карточка, в разрыве — середина между
    // карточками пары, где бы они ни стояли в переставленном ряду (П pair 4998).
    // Пару ищем по id сессий, а не по индексу deriveEvents: окно ленты сдвигает индексы.
    placed() {
      if (!this.on || this.mode === 'joints') return [];
      const out = [];
      this.events.forEach(e => {
        const k = this.posOf.get(e.sid);
        if (e.at === 'gap') {
          // одна сессия пары за окном — метка у той, что в окне (П 4966: ближайший слой),
          // иначе в журнале номер есть, а в сцене метки нет
          const k0 = this.posOf.get(e.sid0);
          if (k === undefined && k0 === undefined) return;
          const a = this.items[k0 !== undefined ? k0 : k], b = this.items[k !== undefined ? k : k0];
          out.push({ e, k: null, z: (a.z + b.z) / 2, dx: (a.x + b.x) / 2 });
        } else {
          if (k === undefined) return;                 // сессия вне окна или вне ряда
          const it = this.items[k];
          out.push({ e, k, z: it.z, dx: it.x });
        }
      });
      return out;
    },
    inByCard() {
      const m = new Map();
      if (!this.gapMode) return m;
      this.placed.forEach(p => {
        if (p.k === null) return;
        if (!m.has(p.k)) m.set(p.k, []);
        m.get(p.k).push(p.e);
      });
      return m;
    },
    marks() {
      const g = this.geom, pins = this.mode === 'pins', beams = !pins;
      const lanes = [null, null, null];
      const sib = new Map();
      this.inByCard.forEach(list => list.forEach((e, j) => sib.set(e.id, [j, list.length])));
      const zs = this.placed.map(p => p.z);
      return this.placed.map((p, pi) => {
        const { e, z } = p;
        const lone = zs.filter((z2, j) => j !== pi && Math.abs(z2 - z) < 60).length < 2;
        const lane = laneOf(lanes, z);
        const labelY = this.floorTop + 6 + lane * 22;
        const dockTop = this.colBottom + 11;            // на 2 px в кромку карточки, без волоска
        const dockH = labelY - dockTop;
        const inCard = p.k !== null;
        const useDock = beams && inCard && dockH > 12;
        const bookmark = pins && !inCard;
        const c = EV[e.kind].c;
        // Столб правее центра, чтобы не закрывать плитки; у события сессии — в границах
        // её карточки, а несколько событий одной карточки разводим вбок, иначе цвета мутнеют.
        let left = this.colLeft + g.colW / 2 + 26 + p.dx, sw = 13;
        if (inCard) {
          // в корешках невыбранная карточка узкая — столб в её ширину (П 5017)
          const w = this.tileW !== null && p.k !== this.selectedIndex ? this.tileW : g.colW;
          const l = this.colLeft - 13 + p.dx;
          const sb = pins ? null : sib.get(e.id);
          if (sb && sb[1] > 1) {
            const pitch = Math.max(8, Math.min(15, (w + 26 - 16) / sb[1]));
            sw = Math.max(6, Math.min(13, Math.round(pitch) - 2));
            left = l + (w + 26) / 2 + (sb[0] - (sb[1] - 1) / 2) * pitch;
          }
          left = Math.min(Math.max(left, l + 9), l + w + 26 - 9);
        }
        let top, h;
        if (pins) { top = bookmark ? g.colTop - 13 : dockTop; h = Math.max(14, labelY - top); }
        else if (useDock) { top = dockTop; h = dockH; }
        else { top = g.colTop - 34; h = this.floorTop - g.colTop + 40 + lane * 22; }
        const on = e.id === this.selected;
        const A = this.mode === 'tint' ? 0.34 : 0.22, A2 = this.mode === 'tint' ? 0.56 : 0.38;
        const style = { left: left + 'px', top: top + 'px', height: h + 'px', '--c': c, transform: BILL(z) };
        if (useDock) Object.assign(style, { '--sw': sw + 'px', '--cA': mix(c, A), '--cB': mix(c, A - 0.1),
          '--cA2': mix(c, A2), '--cB2': mix(c, A2 - 0.14) });
        return { e, z, style, date: dayLabel(e.day), cls: {
          'is-beam': beams, 'is-dock': useDock, 'is-mark': bookmark, 'is-pin': pins, 'is-lab': lone, 'is-on': on } };
      });
    },
    jointMarks() {
      // как П 4895: стыки по соседям текущего ряда, запрет только при сортировке по блоку (jointsOn)
      if (!this.on || this.mode !== 'joints' || !this.jointsOn || this.items.length < 2) return [];
      const g = this.geom, lanes = [null, null, null];
      const x = this.colLeft + g.colW / 2;
      return joints(this.items.map(it => it.unit)).map(c => {
        const a = this.items[c.i - 1].z, b = this.items[c.i].z;
        const span = Math.max(12, b - a), mid = a + span / 2;
        const lane = laneOf(lanes, mid);
        // раньше — зелёный, позже — красный; стык без сдвига серый: событие там — сама пауза
        const col = !c.d ? 'var(--text-muted)' : c.d < 0 ? 'var(--ev-goal)' : 'var(--ev-drop)';
        const pause = c.pause > 0 ? `пауза ${c.pause} ${plural(c.pause, ['день', 'дня', 'дней'])}` : 'в тот же день';
        return { i: c.i, a, mid,
          bar: { left: (x - 4) + 'px', top: (this.floorTop + 4) + 'px', width: '8px', height: span + 'px',
            '--c': col, transform: `translateZ(${-a}px) rotateX(-90deg)` },
          lab: { left: (x + 26) + 'px', top: (this.floorTop + 4) + 'px', height: (8 + lane * 22) + 'px',
            '--c': col, transform: BILL(mid) },
          icon: !c.d ? '⏸' : c.d > 0 ? '↓' : '↑',
          text: c.d ? `блок ${c.hb} → ${c.hb2}` : `блок ${c.hb} без сдвига`,
          pause,
          title: `Между сессиями №${this.num(c.i - 1)} и №${this.num(c.i)}: блок оттока `
            + (c.d ? `${c.hb} → ${c.hb2} (${c.d > 0 ? '+' : ''}${c.d} — ${c.d > 0 ? 'позже' : 'раньше'})`
              : `не сдвинулся, остался ${c.hb}`)
            + ' · ' + (c.pause > 0 ? pause : 'обе сессии в один день') };
      });
    },
  },
  methods: {
    // повторный клик по выбранному снимает выбор
    num(k) { return this.numOf ? this.numOf(this.items[k].unit) : k + 1; },
    pick(id) { this.$emit('select-event', id === this.selected ? null : id); },
  },
};
</script>

<style>
.ins-root .ly-evm{position:absolute; left:0; top:0; width:0; height:0; transform-style:preserve-3d; pointer-events:none;}

.ins-root .ly-ev3{position:absolute; width:0; transform-origin:0 0; pointer-events:none; color:var(--c);}
.ins-root .ly-ev3-stem{position:absolute; left:0; top:0; bottom:0; width:0; border-left:2px dotted var(--c); opacity:.8;}
.ins-root .ly-ev3-cap{position:absolute; left:-8px; top:100%; display:inline-flex; align-items:center; gap:5px;
  white-space:nowrap; font-size:10.5px; font-weight:650; color:var(--c);
  background:var(--surface-1); border:1px solid var(--c); border-radius:999px;
  padding:2px 11px; pointer-events:auto; cursor:pointer;
  box-shadow:0 3px 10px rgba(0,0,0,.10); transition:box-shadow .15s ease;}
.ins-root .ly-ev3-cap b{font-size:10px; font-weight:700; line-height:1;}
.ins-root .ly-ev3-cap u{text-decoration:none; font-size:9px; font-weight:700; line-height:1; min-width:14px; height:14px;
  border-radius:50%; background:var(--c); color:var(--surface-1); display:inline-flex;
  align-items:center; justify-content:center; padding:0 3px; flex:0 0 auto;}
.ins-root .ly-ev3-cap s{text-decoration:none; color:var(--text-muted); font-weight:500;}
.ins-root .ly-ev3-cap:hover{box-shadow:0 0 0 2px var(--c); z-index:5;}
/* без подписи веха — компактный кружок; текст раскрывается при наведении и у выбранной */
.ins-root .ly-ev3 .ly-ev3-txt, .ins-root .ly-ev3 .ly-ev3-cap s{display:none;}
.ins-root .ly-ev3.is-lab .ly-ev3-txt, .ins-root .ly-ev3.is-lab .ly-ev3-cap s,
.ins-root .ly-ev3.is-on .ly-ev3-txt, .ins-root .ly-ev3.is-on .ly-ev3-cap s,
.ins-root .ly-ev3-cap:hover .ly-ev3-txt, .ins-root .ly-ev3-cap:hover s{display:inline;}
.ins-root .ly-ev3:not(.is-lab):not(.is-on) .ly-ev3-cap{height:20px; padding:0 6px 0 3px; gap:4px;}
.ins-root .ly-ev3:not(.is-lab):not(.is-on) .ly-ev3-cap:hover{height:auto; padding:2px 11px;}
.ins-root .ly-ev3.is-on .ly-ev3-cap{background:var(--c); color:#fff; border-color:var(--c);}
/* Ступени прореживания сцены (LayerCamera.thinLabels): сперва кружок с номером и значком,
   потом один номер; текст возвращается по наведению. Без этих стилей «сжатая» подпись
   не уменьшалась и сразу пряталась. */
.ins-root .ly-ev3-cap.ly-mini .ly-ev3-txt, .ins-root .ly-ev3-cap.ly-mini s{display:none !important;}
.ins-root .ly-ev3-cap.ly-mini:hover .ly-ev3-txt{display:inline !important;}
.ins-root .ly-ev3-cap.ly-mini{padding:2px 6px;}
.ins-root .ly-ev3-cap.ly-dotonly{padding:2px; border-color:transparent; background:none; box-shadow:none;}
.ins-root .ly-ev3-cap.ly-dotonly b, .ins-root .ly-ev3-cap.ly-dotonly .ly-ev3-txt, .ins-root .ly-ev3-cap.ly-dotonly s{display:none;}
.ins-root .ly-ev3-cap.ly-dotonly:hover{background:var(--surface-1); border-color:var(--c); padding:2px 6px;}
.ins-root .ly-ev3-cap.ly-dotonly:hover b{display:inline;}
.ins-root .ly-ev3.ly-hushed{visibility:hidden;}
.ins-root .ly-ev3.is-on .ly-ev3-cap u{background:var(--surface-1); color:var(--c);}
.ins-root .ly-ev3.is-on .ly-ev3-cap s{color:rgba(255,255,255,.8);}
/* «вехи»: пунктир от подписи к своей карточке; в разрыве — закладка между карточками */
.ins-root .ly-ev3.is-pin .ly-ev3-stem{border-left-width:2px; opacity:.85;}
.ins-root .ly-ev3.is-mark .ly-ev3-stem{border-left:0; width:3px; left:-1.5px; border-radius:2px; opacity:.62;
  background:repeating-linear-gradient(to bottom, var(--c) 0 7px, rgba(0,0,0,0) 7px 13px);}
.ins-root .ly-ev3.is-mark.is-on .ly-ev3-stem{opacity:1; width:4px; left:-2px;}
/* столб от пола — подача «заливки» и «картотеки» */
.ins-root .ly-ev3.is-beam .ly-ev3-stem{left:-6px; width:13px; border:0; border-radius:7px; opacity:.3;
  background:linear-gradient(to bottom, rgba(0,0,0,0), var(--c) 82%, var(--c));}
.ins-root .ly-ev3.is-beam.is-on .ly-ev3-stem{opacity:.62;}
/* столб события сессии упирается в нижнюю кромку карточки тем же цветом, каким начинается заливка */
.ins-root .ly-ev3.is-beam.is-dock .ly-ev3-stem{opacity:1; border-radius:0 0 7px 7px;
  width:var(--sw,13px); left:calc(var(--sw,13px) / -2);
  background:linear-gradient(to top, var(--cB), var(--cA));}
.ins-root .ly-ev3.is-beam.is-dock.is-on .ly-ev3-stem{background:linear-gradient(to top, var(--cB2), var(--cA2));}
/* «стыки»: скобка на полу в разрыве между соседними карточками */
.ins-root .ly-evm-jnt{position:absolute; pointer-events:none; border-radius:4px; transform-origin:50% 0;
  background:linear-gradient(to bottom, var(--c), rgba(0,0,0,0)); opacity:.5;}
.ins-root .ly-ev3.is-joint .ly-ev3-stem{border-left-style:solid; opacity:.55;}
.ins-root .ly-ev3.is-joint .ly-ev3-cap{cursor:default;}
</style>
